// SocketProvider.js - UPDATED VERSION
import React, { useEffect, useRef } from "react";
import { socket } from "../common/socket";

const EVENT_MAP = [
  { socketEvent: "connect", browserEvent: "socket:connected" },
  { socketEvent: "disconnect", browserEvent: "socket:disconnected" },
  { socketEvent: "newMessage", browserEvent: "socket:newMessage" },
  { socketEvent: "chatList:update", browserEvent: "socket:chatListUpdate" },
  { socketEvent: "userTyping", browserEvent: "socket:userTyping" },
  { socketEvent: "messagesRead", browserEvent: "socket:messagesRead" },
  { socketEvent: "messageEdited", browserEvent: "socket:messageEdited" },
  { socketEvent: "messagesDeleted", browserEvent: "socket:messagesDeleted" },
  { socketEvent: "error", browserEvent: "socket:error" }
];

const getStoredToken = () => {
  if (typeof window === "undefined") return null;
  try {
    const authRaw = localStorage.getItem("authUser");
    if (!authRaw) return null;
    const auth = JSON.parse(authRaw);
    return auth?.token || null;
  } catch (error) {
    console.error("❌ Error reading auth token:", error);
    return null;
  }
};

const SocketProvider = ({ children }) => {
  const listenersRef = useRef([]);
  const tokenRef = useRef(getStoredToken());
  const connectionAttemptRef = useRef(false);

  useEffect(() => {
    const syncSocketAuth = () => {
      const storedToken = getStoredToken();
      console.log("🔄 [SocketProvider] Token sync check:", {
        hasStoredToken: !!storedToken,
        hasPreviousToken: !!tokenRef.current,
        tokensMatch: tokenRef.current === storedToken,
        socketConnected: socket.connected
      });

      if (tokenRef.current === storedToken && socket.connected) {
        console.log("✅ [SocketProvider] Token unchanged and socket connected, skipping");
        return;
      }

      tokenRef.current = storedToken;

      if (storedToken) {
        console.log("🔐 [SocketProvider] Updating auth token and connecting");
        socket.auth = { ...(socket.auth || {}), token: storedToken };

        // Only connect if not already connected or connecting
        if (!socket.connected && !connectionAttemptRef.current) {
          connectionAttemptRef.current = true;
          console.log("🔌 [SocketProvider] Attempting socket connection");
          socket.connect();

          // Reset connection attempt flag after timeout
          setTimeout(() => {
            connectionAttemptRef.current = false;
          }, 5000);
        } else {
          console.log("ℹ️ [SocketProvider] Socket already connected or connecting attempt in progress");
        }
      } else {
        console.log("🚪 [SocketProvider] No auth token, disconnecting socket");
        if (socket.connected) {
          socket.disconnect();
        }
        connectionAttemptRef.current = false;
      }
    };

    // Initial sync
    syncSocketAuth();

    const handleTokenEvent = () => {
      console.log("🔄 [SocketProvider] Token change detected, syncing socket");
      syncSocketAuth();
    };

    // Listen for storage changes and custom auth events
    window.addEventListener("storage", handleTokenEvent);
    window.addEventListener("auth:token", handleTokenEvent);

    // Also listen for socket connection state changes
    const handleConnect = () => {
      console.log("✅ [SocketProvider] Socket connected successfully");
      connectionAttemptRef.current = false;
    };

    const handleConnectError = (error) => {
      console.error("❌ [SocketProvider] Socket connection error:", error);
      connectionAttemptRef.current = false;
    };

    socket.on("connect", handleConnect);
    socket.on("connect_error", handleConnectError);

    return () => {
      window.removeEventListener("storage", handleTokenEvent);
      window.removeEventListener("auth:token", handleTokenEvent);
      socket.off("connect", handleConnect);
      socket.off("connect_error", handleConnectError);
    };
  }, []);

  useEffect(() => {
    const registerListener = ({ socketEvent, browserEvent }) => {
      const handler = (payload) => {
        let detail = payload;
        if (socketEvent === "connect") {
          detail = { socketId: socket.id };
        } else if (socketEvent === "disconnect") {
          detail = payload || {};
        }

        console.log(`🔔 [SocketProvider] Forwarding event: ${socketEvent} -> ${browserEvent}`, {
          payload: payload,
          socketConnected: socket.connected
        });

        window.dispatchEvent(new CustomEvent(browserEvent, { detail }));
      };

      socket.on(socketEvent, handler);
      listenersRef.current.push({ socketEvent, handler });
    };

    EVENT_MAP.forEach(registerListener);

    return () => {
      listenersRef.current.forEach(({ socketEvent, handler }) => {
        socket.off(socketEvent, handler);
      });
      listenersRef.current = [];
    };
  }, []);

  useEffect(() => {
    const checkConnection = () => {
      if (!socket.connected) {
        console.log("🔄 Socket not connected, attempting reconnect...");
        const storedToken = getStoredToken();
        if (storedToken) {
          socket.auth = { token: storedToken };
          socket.connect();
        }
      }
    };

    // Check connection every 5 seconds
    const interval = setInterval(checkConnection, 5000);

    return () => clearInterval(interval);
  }, []);

  return children;
};

export default SocketProvider;